import React, { useMemo, Fragment } from 'react';
import T from 'prop-types';
import { first, get } from 'lodash-es';

import Button from '../../../../components/Buttons/Button';
import { SmartChart } from '../../../../components/Chart';
import Skeleton from '../../../../components/Skeleton';

import { chartYearConfig } from '../config/finance/charts';
import { ISODateFormat, format, yesterday } from '../../../../utils/date';

import ChartTable from './ChartTable';

const emptyArray = [];

const YearChart = ({
  loading,
  series,
  metric,
  granularity,
  overPeriod,
  rotateLabels,
  height = 276,
  skeletonHeight = 276,
  animation,
  hasTable = false,
  showTable,
  yGridLine = false,
  showWatermark = false,
  variant,
  toggleSize = 'sm',
  maxDate,
  subDates,
  footer,
  onShowTable,
}) => {
  const seriesByMetric = get(series, metric, emptyArray);

  const filters = useMemo(() => ({
    granularity,
    period_from_date: seriesByMetric.length > 0
      ? format(first(seriesByMetric)[0], ISODateFormat)
      : yesterday(),
    period_to_date: yesterday(),
  }), [seriesByMetric, granularity]);

  const chartConfig = useMemo(() => (
    chartYearConfig(seriesByMetric, {
      granularity,
      overPeriod,
      rotateLabels,
      height,
      animation,
      yGridLine,
      variant,
      maxDate,
      subDates,
    })
  ), [
    seriesByMetric, granularity, overPeriod, rotateLabels, height, yGridLine, variant, subDates,
  ]);

  const seriesData = useMemo(() => {
    const hasData = seriesByMetric.some(s => s[1] !== null);

    return ({
      [metric]: hasData ? seriesByMetric : emptyArray,
    });
  }, [metric, seriesByMetric]);

  const metaSeriesData = useMemo(() => ({
    [metric]: {
      color: '#3E8EDE',
      negativeColor: '#E33963',
      type: 'column',
      visible: true,
      name: metric,
      decorateColor: ({ y }) => (y >= 0 ? '#3E8EDE' : '#E33963'), // for partial period
    },
  }), [metric]);

  return (
    <div className="year-chart-wrapper">
      {loading
        ? <Skeleton className="year-chart-wrapper-box" height={skeletonHeight} />
        : (
          <div className="year-chart-wrapper-box">
            <SmartChart
              chartId="year-chart"
              series={seriesData}
              metaSeriesData={metaSeriesData}
              config={chartConfig}
              filters={filters}
              allowPartialPeriods
            />
          </div>
        )}
      {hasTable && (
        <Fragment>
          <div className="table-toggle-wrapper">
            {!loading && (
              <Button
                size={toggleSize || undefined}
                shape="square"
                variant="secondary"
                className="table-toggle-button"
                active={showTable}
                onClick={onShowTable}
              >
                <i className="hicon-list-button" />
              </Button>
            )}
          </div>
          <ChartTable
            show={showTable}
            loading={loading}
            variant="year"
            tableData={seriesData[metric]}
          />
        </Fragment>
      )}
      {(showWatermark || footer) && (
        <div className="year-chart-footer">
          {showWatermark && <div className="highcharts-watermark" />}
          {footer}
        </div>
      )}
    </div>
  );
};

YearChart.propTypes = {
  series: T.object,
  metric: T.string,
  granularity: T.string,
  overPeriod: T.string,
  rotateLabels: T.bool,
  loading: T.bool,
  height: T.number,
  skeletonHeight: T.number,
  animation: T.bool,
  hasTable: T.bool,
  yGridLine: T.bool,
  showWatermark: T.bool,
  variant: T.oneOf(['qv', 'v1', 'v2']),
  toggleSize: T.string,
  showTable: T.bool,
  maxDate: T.string,
  subDates: T.object,
  footer: T.node,
  onShowTable: T.func,
};

export default YearChart;
